/**
 * LoadDetailsMobileView
 *
 * Mobile-only card list for the ASN Gap "Load Details" tab.
 * Each load record is rendered as a card with:
 *   - PO / BOL Number as a permanent title (never counts as a detail field)
 *   - Three user-selected detail fields shown as metric boxes
 *
 * A "Report Builder" panel (collapsed by default) lets the user pick which
 * three fields to display. It reuses the layout / styling / selection pattern
 * from the Analytics Hub KPIOnePagerV2 mobile view, but its column list is
 * driven exclusively by LOAD_DETAILS_COLUMNS (minus the excluded keys below).
 *
 * Nothing in this file touches the web table, the Columns dropdown, API calls,
 * response mapping, sorting, filtering, or pagination — all of those remain
 * owned by LoadDetails.tsx.
 */

import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';
import { ASNLoadDetailRow } from '../api/asnOverviewKpiApi';
import {
  formatLoadDetailsCellValue as formatCellValue,
  LOAD_DETAILS_COLUMNS,
  LoadDetailsColumnDef,
  LoadDetailsColumnKey,
} from '../constants/loadDetailsColumns';
import OnePagerMetricCard from '../../../../AnalyticsHub/components/kpiOnePager/OnePagerMetricCard';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * The column that is always the card title on mobile.
 * It is never shown in the detail metric boxes and never appears in the
 * Report Builder picker.
 */
const TITLE_COLUMN_KEY: LoadDetailsColumnKey = 'pobol';

/**
 * Columns excluded from the Report Builder picker and from mobile cards.
 * These are the region / market / location grain dimensions that the prompt
 * explicitly prohibits from the mobile Load Details Report Builder.
 */
const EXCLUDED_KEYS = new Set<LoadDetailsColumnKey>([
  TITLE_COLUMN_KEY,  // always the card title, never a detail field
  'shipReg',         // Shipping Location Region / Unit
  'shipMkt',         // Shipping Location Market / POD
  'recvReg',         // Receiving Region / Unit
  'recvMkt',         // Receiving Market / POD
]);

/**
 * Columns available in the Report Builder, in the existing LOAD_DETAILS_COLUMNS
 * order (preserves the "existing column order" requirement).
 */
const PICKER_COLUMNS: LoadDetailsColumnDef[] = LOAD_DETAILS_COLUMNS.filter(
  (col) => !EXCLUDED_KEYS.has(col.key),
);

/** Maximum number of detail fields shown per card. */
const MAX_FIELDS = 3;

/** Default three fields selected on first render. */
const DEFAULT_SELECTED: LoadDetailsColumnKey[] = ['recvDate', 'shipType', 'cases'];

// ─── Props ────────────────────────────────────────────────────────────────────

export interface LoadDetailsMobileViewProps {
  rows: ASNLoadDetailRow[];
  loading: boolean;
  hasData: boolean;
  message?: string;
  /** Passed through to the footer unchanged. */
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const LoadDetailsMobileView: React.FC<LoadDetailsMobileViewProps> = ({
  rows,
  loading,
  hasData,
  message,
  totalCount,
  pageNumber,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
}) => {
  // ─── Detail modal state ───────────────────────────────────────────────
  const [selectedRow, setSelectedRow] = useState<ASNLoadDetailRow | null>(null);

  const handleCardPress = useCallback((row: ASNLoadDetailRow) => {
    setSelectedRow(row);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedRow(null);
  }, []);

  // ─── Report Builder state ────────────────────────────────────────────
  const [isBuilderExpanded, setIsBuilderExpanded] = useState(false);

  // Pending selection (while builder is open)
  const [pendingFields, setPendingFields] = useState<LoadDetailsColumnKey[]>(DEFAULT_SELECTED);
  // Applied selection (drives the cards)
  const [appliedFields, setAppliedFields] = useState<LoadDetailsColumnKey[]>(DEFAULT_SELECTED);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleToggleBuilder = useCallback(() => {
    setIsBuilderExpanded((prev) => !prev);
  }, []);

  const handleFieldToggle = useCallback((key: LoadDetailsColumnKey) => {
    setPendingFields((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      if (prev.length >= MAX_FIELDS) {
        return prev; // already at max — no-op
      }
      return [...prev, key];
    });
  }, []);

  const handleApply = useCallback(() => {
    setAppliedFields(pendingFields);
    setIsBuilderExpanded(false);
  }, [pendingFields]);

  const handleReset = useCallback(() => {
    setPendingFields(DEFAULT_SELECTED);
    setAppliedFields(DEFAULT_SELECTED);
    setIsBuilderExpanded(false);
  }, []);

  // ─── Derived data ─────────────────────────────────────────────────────

  const appliedColumnDefs = PICKER_COLUMNS.filter((col) =>
    appliedFields.includes(col.key),
  );

  const titleColDef = LOAD_DETAILS_COLUMNS.find((c) => c.key === TITLE_COLUMN_KEY)!;

  const hasRows = hasData && rows.length > 0;
  const startRow = !hasRows ? 0 : (pageNumber - 1) * pageSize + 1;
  const endRow = !hasRows ? 0 : startRow + rows.length - 1;

  // ─── Render helpers ───────────────────────────────────────────────────

  const renderCards = () => {
    if (loading) {
      return (
        <View style={styles.stateContainer}>
          <ActivityIndicator size="large" color="#005CBC" />
          <Text style={styles.stateText}>Loading loads...</Text>
        </View>
      );
    }

    if (!hasRows) {
      return (
        <View style={styles.stateContainer}>
          <MaterialIcons name="table-chart" size={40} color="#ADB5BD" />
          <Text style={styles.stateText}>
            {message || 'No load data available for the selected filters'}
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.cardsContainer}>
        {rows.map((row, idx) => {
          const title = formatCellValue(row[titleColDef.field]) || '—';

          const metrics = appliedColumnDefs.map((col) => {
            let value: string;

            if (col.key === 'asn') {
              value = formatCellValue(row.asnAvailable);
            } else {
              value = formatCellValue(row[col.field], col.numeric);
            }

            return {
              label: col.label,
              value: value || '—',
            };
          });

          return (
            <OnePagerMetricCard
              key={`${row.poBoLNumber ?? 'row'}-${idx}`}
              title={title}
              subtitle=""
              metrics={metrics}
              onPress={() => handleCardPress(row)}
            />
          );
        })}
      </View>
    );
  };

  const renderPagination = () => {
    if (!hasRows) return null;

    return (
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          {`${startRow}–${endRow} of ${totalCount} loads`}
        </Text>

        {totalPages > 1 && (
          <View style={styles.pager}>
            <TouchableOpacity
              style={[styles.pagerBtn, (pageNumber <= 1 || loading) && styles.pagerBtnDisabled]}
              onPress={() => onPageChange(1)}
              disabled={pageNumber <= 1 || loading}
              testID="asn-gap-ld-mobile-page-first"
            >
              <MaterialIcons
                name="first-page"
                size={18}
                color={pageNumber <= 1 ? '#B0B7C3' : '#034895'}
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.pagerBtn, (pageNumber <= 1 || loading) && styles.pagerBtnDisabled]}
              onPress={() => onPageChange(Math.max(1, pageNumber - 1))}
              disabled={pageNumber <= 1 || loading}
              testID="asn-gap-ld-mobile-page-prev"
            >
              <MaterialIcons
                name="chevron-left"
                size={18}
                color={pageNumber <= 1 ? '#B0B7C3' : '#034895'}
              />
            </TouchableOpacity>

            <Text style={styles.pagerLabel}>
              {`${pageNumber} / ${totalPages}`}
            </Text>

            <TouchableOpacity
              style={[styles.pagerBtn, (pageNumber >= totalPages || loading) && styles.pagerBtnDisabled]}
              onPress={() => onPageChange(Math.min(totalPages, pageNumber + 1))}
              disabled={pageNumber >= totalPages || loading}
              testID="asn-gap-ld-mobile-page-next"
            >
              <MaterialIcons
                name="chevron-right"
                size={18}
                color={pageNumber >= totalPages ? '#B0B7C3' : '#034895'}
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.pagerBtn, (pageNumber >= totalPages || loading) && styles.pagerBtnDisabled]}
              onPress={() => onPageChange(totalPages)}
              disabled={pageNumber >= totalPages || loading}
              testID="asn-gap-ld-mobile-page-last"
            >
              <MaterialIcons
                name="last-page"
                size={18}
                color={pageNumber >= totalPages ? '#B0B7C3' : '#034895'}
              />
            </TouchableOpacity>
          </View>
        )}

        <View style={styles.pageSizeRow}>
          {[25, 50, 100].map((size) => (
            <TouchableOpacity
              key={size}
              style={[styles.pageSizeBtn, pageSize === size && styles.pageSizeBtnActive, loading && styles.pagerBtnDisabled]}
              onPress={() => onPageSizeChange(size)}
              disabled={loading}
              testID={`asn-gap-ld-mobile-page-size-${size}`}
            >
              <Text
                style={[
                  styles.pageSizeBtnText,
                  pageSize === size && styles.pageSizeBtnTextActive,
                ]}
              >
                {size}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );
  };

  // ─── Detail modal ─────────────────────────────────────────────────────

  const renderDetailModal = () => {
    if (!selectedRow) return null;
    const pobol = formatCellValue(selectedRow.poBoLNumber) || '—';

    return (
      <Modal
        visible
        transparent
        animationType="fade"
        onRequestClose={handleCloseModal}
        testID="asn-gap-ld-mobile-detail-modal"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            {/* ── Modal header ── */}
            <View style={styles.modalHeader}>
              <View style={styles.modalTitleBlock}>
                <Text style={styles.modalPobol} numberOfLines={1}>
                  {pobol}
                </Text>
                <Text style={styles.modalPobolLabel}>PO/BOL NUMBER</Text>
              </View>
              <TouchableOpacity
                style={styles.modalCloseBtn}
                onPress={handleCloseModal}
                accessibilityRole="button"
                accessibilityLabel="Close"
                testID="asn-gap-ld-mobile-detail-close"
              >
                <MaterialIcons name="close" size={22} color="#495057" />
              </TouchableOpacity>
            </View>

            {/* ── Scrollable field list ── */}
            <ScrollView
              style={styles.modalScroll}
              contentContainerStyle={styles.modalScrollContent}
              showsVerticalScrollIndicator
            >
              {LOAD_DETAILS_COLUMNS.map((col) => {
                // Format the value using the same helper as the card/table
                let value: string;
                if (col.key === 'asn') {
                  value = formatCellValue(selectedRow.asnAvailable);
                } else {
                  value = formatCellValue(selectedRow[col.field], col.numeric);
                }

                return (
                  <View key={col.key} style={styles.modalRow}>
                    <Text style={styles.modalRowLabel} numberOfLines={1}>
                      {col.label}
                    </Text>
                    <Text style={styles.modalRowValue} numberOfLines={1}>
                      {value || '—'}
                    </Text>
                  </View>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <ScrollView
      style={styles.root}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {/* ── Report Builder ── */}
      <View style={styles.builderCard}>
        {/* Header row — always visible */}
        <TouchableOpacity
          style={styles.builderHeader}
          onPress={handleToggleBuilder}
          activeOpacity={0.7}
          testID="asn-gap-ld-mobile-builder-toggle"
        >
          <View style={styles.builderHeaderLeft}>
            <View style={styles.builderIconBox}>
              <MaterialIcons name="bar-chart" size={24} color="#FFFFFF" />
            </View>
            <View style={styles.builderHeaderText}>
              <Text style={styles.builderTitle}>Report Builder</Text>
              <Text style={styles.builderSubtitle}>
                {appliedFields.length}{' '}
                {appliedFields.length === 1 ? 'field' : 'fields'} selected
              </Text>
            </View>
          </View>
          <MaterialIcons
            name={isBuilderExpanded ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
            size={24}
            color="#005CBC"
          />
        </TouchableOpacity>

        {/* Expanded content */}
        {isBuilderExpanded && (
          <View style={styles.builderBody}>
            <Text style={styles.builderInstruction}>
              Select up to 3 fields to display on each card. PO / BOL Number is
              always shown as the card title.
            </Text>

            {/* Field pills */}
            <View style={styles.pillsWrap}>
              {PICKER_COLUMNS.map((col) => {
                const isSelected = pendingFields.includes(col.key);
                const isDisabled =
                  !isSelected && pendingFields.length >= MAX_FIELDS;
                return (
                  <TouchableOpacity
                    key={col.key}
                    style={[
                      styles.pill,
                      isSelected && styles.pillSelected,
                      isDisabled && styles.pillDisabled,
                    ]}
                    onPress={() => handleFieldToggle(col.key)}
                    disabled={isDisabled}
                    activeOpacity={0.7}
                    testID={`asn-gap-ld-mobile-field-${col.key}`}
                  >
                    <Text
                      style={[
                        styles.pillText,
                        isSelected && styles.pillTextSelected,
                        isDisabled && styles.pillTextDisabled,
                      ]}
                    >
                      {col.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            {/* Counter */}
            <Text style={styles.counter}>
              <Text style={styles.counterHighlight}>{pendingFields.length}</Text>
              {` of ${MAX_FIELDS} selected`}
            </Text>

            {/* Actions */}
            <View style={styles.builderActions}>
              <TouchableOpacity
                style={styles.resetBtn}
                onPress={handleReset}
                activeOpacity={0.7}
                testID="asn-gap-ld-mobile-builder-reset"
              >
                <Text style={styles.resetBtnText}>Reset</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.applyBtn}
                onPress={handleApply}
                activeOpacity={0.7}
                testID="asn-gap-ld-mobile-builder-apply"
              >
                <Text style={styles.applyBtnText}>Apply</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>

      {/* ── Cards ── */}
      <View style={styles.cardsSection}>{renderCards()}</View>

      {/* ── Pagination ── */}
      {renderPagination()}

      {/* ── Detail modal (rendered outside the scroll tree so it covers all content) ── */}
      {renderDetailModal()}
    </ScrollView>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  scrollContent: {
    paddingHorizontal: 14,
    paddingTop: 4,
    paddingBottom: 16,
    gap: 10,
  },

  // ── Report Builder ──────────────────────────────────────────────────────────
  builderCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  builderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  builderHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  builderIconBox: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: '#005CBC',
    alignItems: 'center',
    justifyContent: 'center',
  },
  builderHeaderText: {
    gap: 2,
  },
  builderTitle: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    fontWeight: '600',
    color: '#005CBC',
  },
  builderSubtitle: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: '#838383',
  },
  builderBody: {
    paddingHorizontal: 14,
    paddingBottom: 12,
    borderTopWidth: 1,
    borderTopColor: '#E9ECEF',
  },
  builderInstruction: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    fontStyle: 'italic',
    color: '#9E9E9E',
    marginTop: 12,
    marginBottom: 12,
    lineHeight: 18,
  },
  pillsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 10,
  },
  pill: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    backgroundColor: '#E9ECEF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  pillSelected: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },
  pillDisabled: {
    opacity: 0.45,
  },
  pillText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#6C757D',
  },
  pillTextSelected: {
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
  pillTextDisabled: {
    color: '#9AA1AB',
  },
  counter: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#6C757D',
    textAlign: 'right',
    marginBottom: 10,
  },
  counterHighlight: {
    color: '#005CBC',
    fontFamily: FontFamily.bold,
  },
  builderActions: {
    flexDirection: 'row',
    gap: 12,
  },
  resetBtn: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#005CBC',
    alignItems: 'center',
  },
  resetBtnText: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    color: '#005CBC',
    fontWeight: '700',
  },
  applyBtn: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: '#005CBC',
    borderRadius: 24,
    alignItems: 'center',
  },
  applyBtnText: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    color: '#FFFFFF',
    fontWeight: '700',
  },

  // ── Cards ───────────────────────────────────────────────────────────────────
  cardsSection: {
    // gap applied via ScrollView contentContainerStyle
  },
  cardsContainer: {
    gap: 10,
  },

  // ── States ──────────────────────────────────────────────────────────────────
  stateContainer: {
    paddingVertical: 24,
    paddingHorizontal: 16,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    gap: 12,
  },
  stateText: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#6A6D71',
    textAlign: 'center',
    fontStyle: 'italic',
  },

  // ── Footer / Pagination ─────────────────────────────────────────────────────
  footer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 4,
    rowGap: 8,
  },
  footerText: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#6A6D71',
  },
  pager: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  pagerBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pagerBtnDisabled: {
    opacity: 0.4,
  },
  pagerLabel: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#6A6D71',
    marginHorizontal: 4,
  },
  pageSizeRow: {
    flexDirection: 'row',
    gap: 4,
  },
  pageSizeBtn: {
    height: 28,
    paddingHorizontal: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#D0D0D0',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  pageSizeBtnActive: {
    backgroundColor: '#EFF5FC',
    borderColor: '#005CBC',
  },
  pageSizeBtnText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#6A6D71',
  },
  pageSizeBtnTextActive: {
    color: '#005CBC',
    fontFamily: FontFamily.bold,
  },

  // ── Detail modal ─────────────────────────────────────────────────────────────
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.38)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 18,
  },
  modalCard: {
    width: '100%',
    maxHeight: '88%',
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#EEF1F4',
  },
  modalTitleBlock: {
    flex: 1,
    paddingRight: 12,
  },
  modalPobol: {
    fontSize: 17,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1D21',
  },
  modalPobolLabel: {
    marginTop: 3,
    fontSize: 11,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#9AA1AB',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  modalCloseBtn: {
    padding: 4,
    marginTop: -2,
  },
  modalScroll: {
    flexShrink: 1,
  },
  modalScrollContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    paddingBottom: 20,
  },
  modalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F2F5',
  },
  modalRowLabel: {
    flex: 1,
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#6A6D71',
    paddingRight: 12,
  },
  modalRowValue: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    fontWeight: '600',
    color: '#1A1D21',
    textAlign: 'right',
    flexShrink: 1,
    maxWidth: '55%',
  },
});

LoadDetailsMobileView.displayName = 'LoadDetailsMobileView';

export default React.memo(LoadDetailsMobileView);
